import { Navigate, useLocation } from 'react-router-dom';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { FeatureName } from '@/config/features.config';
import { getTenantFromDomain } from '@/lib/tenant-resolver';
import { ROUTES, storePath } from '@/config/routes.config';
import { getHostname, isAppDomain, isLocalhost } from '@/lib/domain';
// Polaris imports
import {
  Page,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Box,
  Spinner,
  Icon,
} from '@/lib/polaris';
import { LockIcon } from '@/lib/polaris-icons';

interface FeatureProtectedRouteProps {
  children: React.ReactNode;
  feature: FeatureName;
  /**
   * Si el módulo está deshabilitado:
   * - `string` que empiece por `/`: redirección SPA (con prefijo `/store/:slug` cuando aplica).
   * - Otro ReactNode: se renderiza tal cual.
   */
  fallback?: React.ReactNode;
  publicRoute?: boolean; // Si es true, no requiere autenticación
}

export function FeatureProtectedRoute({
  children,
  feature,
  fallback,
  publicRoute = false,
}: FeatureProtectedRouteProps) {
  const location = useLocation();
  const storeNavigate = useStoreNavigate();
  const { storeSlug } = useAuth();
  const { isFeatureEnabled, loading, isDevMode } = useFeatureFlags();
  
  // Las rutas de ecommerce solo en subdominios de tenant (dulti.smarty.lat), no en panel (admin.smarty.lat)
  if (publicRoute && feature === 'ecommerce') {
    const hostname = getHostname();
    
    if (isAppDomain(hostname)) {
      return <Navigate to="/login" replace />;
    }
    
    if (isLocalhost(hostname) && !hostname.includes('.smarty.lat') && !hostname.includes('.smarty.local')) {
      const subdomain = getTenantFromDomain();
      if (!subdomain) {
        return <Navigate to="/login" replace />;
      }
    }
    
    // Bloquear si hay parámetro ?tenant= (no permitir acceso por URL directa)
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('tenant')) {
      // Remover parámetro y redirigir
      urlParams.delete('tenant');
      const newUrl = location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      return <Navigate to={newUrl} replace />;
    }
  }
  
  // Las rutas públicas del ecommerce están en subdominios (dulti.smarty.lat), no en admin.smarty.lat
  const isPublicRoute = publicRoute || !!getTenantFromDomain();
  
  // Para rutas públicas, permitir acceso inmediatamente sin esperar autenticación
  if (isPublicRoute) {
    // Las rutas públicas del ecommerce siempre deben estar disponibles
    // No requieren autenticación ni verificación de feature flags
    return <>{children}</>;
  }
  
  if (loading) {
    return (
      <Page>
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  // En modo desarrollo, siempre permitir acceso
  if (isDevMode) {
    return <>{children}</>;
  }

  // En producción, verificar feature flag
  if (!isFeatureEnabled(feature)) {
    if (fallback != null) {
      if (typeof fallback === 'string' && fallback.startsWith('/')) {
        const to = storeSlug ? storePath(storeSlug, fallback) : fallback;
        return <Navigate to={to} replace />;
      }
      return <>{fallback}</>;
    }

    return (
      <Page>
        <Box padding="800">
          <InlineStack align="center">
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="200" align="center">
                  <Icon source={LockIcon} tone="subdued" />
                  <Text variant="headingMd" fontWeight="semibold" as="h2">
                    Módulo no disponible
                  </Text>
                </InlineStack>
                <Text variant="bodyMd" tone="subdued" as="p">
                  Este módulo no está incluido en tu plan actual.
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  Para acceder a esta funcionalidad, por favor actualiza tu plan de suscripción.
                </Text>
                <Button
                  onClick={() => storeNavigate(ROUTES.SETTINGS)}
                  variant="primary"
                >
                  Ver Planes
                </Button>
              </BlockStack>
            </Card>
          </InlineStack>
        </Box>
      </Page>
    );
  }

  return <>{children}</>;
}
